import { useEffect, useState } from 'react';
import { NATIVE_OR_SIM } from '../lib/proximity/native/simPhone';
import { LynxLocation } from '../lib/proximity/native/LynxLocation';

/** Heading fires many times a second; only a turn this big is worth a render (as useMapSelfLocation). */
const HEADING_STEP_DEG = 3;
const OWNER = 'compass';

// Start/stop one after another, so a quick remount's stop never lands after its start (useMapSelfLocation, 2026-09-25).
let nativeQueue = Promise.resolve();
function queueNative(fn) {
  const run = nativeQueue.then(fn);
  nativeQueue = run.catch(() => {});
  return run;
}

/**
 * The phone's compass alone, for the welcome flow's Map and Find (Sam, 2026-10-26: the made-up friends should turn with
 * the phone though you are nowhere near Portola). Core Location's heading needs no location fix; it never asks for
 * permission either - with location not yet decided it stays off rather than put the prompt in the middle of the tour.
 * Null on the web and until the first reading.
 */
export default function useDeviceHeading(enabled) {
  const [headingDeg, setHeadingDeg] = useState(null);
  useEffect(() => {
    if (!enabled || !NATIVE_OR_SIM) return undefined;
    let cancelled = false;
    const cleanups = [];
    (async () => {
      let status = 'unknown';
      try {
        ({ status } = await LynxLocation.getAuthorizationStatus());
      } catch {
        /* treat as unknown */
      }
      if (cancelled || status === 'notDetermined') return;
      const head = await LynxLocation.addListener('headingUpdate', (e) => {
        const deg = e.trueHeading >= 0 ? e.trueHeading : e.magneticHeading;
        if (!Number.isFinite(deg)) return;
        setHeadingDeg((prev) =>
          prev == null || Math.abs(((deg - prev + 540) % 360) - 180) >= HEADING_STEP_DEG
            ? deg
            : prev,
        );
      });
      cleanups.push(() => head.remove());
      await queueNative(() => LynxLocation.startWatching({ owner: OWNER }));
      cleanups.push(() => queueNative(() => LynxLocation.stopWatching({ owner: OWNER })));
      if (cancelled)
        cleanups.splice(0).forEach((fn) => {
          void fn();
        });
    })().catch((err) => console.warn('[compass] failed:', err));
    return () => {
      cancelled = true;
      cleanups.splice(0).forEach((fn) => {
        void fn();
      });
    };
  }, [enabled]);
  return enabled ? headingDeg : null;
}
